import {
  anonymousAuth,
  type AuthRepository,
  createAuthRepository,
  createTalerClient,
  type TokenProvider,
} from '@taler/api-client'
import type { Clock } from '@taler/domain'
import type { AppConfig } from '../config.ts'
import { createCrossTabAuth, type CrossTabAuth } from './cross-tab.ts'
import { createDevSignIn, type DevSignIn } from './dev-sign-in.ts'
import { createSignIn, type SignIn } from './sign-in.ts'
import { createTokenProvider } from './token-provider.ts'
import { createTokenStore, type TokenStore } from './token-store.ts'
import { createTransactionStore } from './transaction-store.ts'

export interface AuthOptions {
  readonly config: AppConfig
  readonly clock: Clock
  readonly storage: Storage
  readonly navigate: (url: string) => void
}

export interface Auth {
  readonly store: TokenStore
  readonly provider: TokenProvider
  readonly repository: AuthRepository
  readonly signIn: SignIn
  readonly crossTab: CrossTabAuth
  /** Present only in a dev-server build; absent (dead-code-eliminated) in production. */
  readonly dev: DevSignIn | undefined
  signOut(): Promise<void>
}

/**
 * Wires the auth seam once. The token endpoints go through their own client, built with
 * `anonymousAuth`: they carry no bearer token, and a 401 from them must not trigger the
 * renewal that calls them.
 */
export function createAuth(options: AuthOptions): Auth {
  const crossTab = createCrossTabAuth((event) => {
    if (event === 'signed-out') store.clear({ announce: false })
  })
  const store = createTokenStore({ clock: options.clock, broadcast: crossTab.broadcast })
  const client = createTalerClient({ baseUrl: options.config.apiBaseUrl, auth: anonymousAuth })

  const repository = createAuthRepository({ client, now: options.clock.now })

  // import.meta.env.DEV is a build-time constant Vite folds to `false` in production, so
  // this whole branch - and its only caller of the dev-token endpoint - is dead-code-eliminated
  // from a production build, not merely unreachable in it.
  const rawDev = import.meta.env.DEV ? createDevSignIn(client, options.clock) : undefined
  const devSignInEmail = options.config.devSignInEmail

  // Renewal must only take the dev-token path for a session that was actually signed in
  // through it - a session signed in via Google against this same client (e.g. the mock
  // server, which has no /dev/token handler) must keep renewing through repository.refresh().
  let viaDevSignIn = false
  store.subscribe((next) => {
    if (next === null) viaDevSignIn = false
  })
  const dev: DevSignIn | undefined =
    rawDev === undefined
      ? undefined
      : {
          async signIn(email) {
            const token = await rawDev.signIn(email)
            viaDevSignIn = true
            return token
          },
        }

  const provider = createTokenProvider({
    store,
    renew: async () => {
      try {
        // A dev token has no refresh cookie; re-minting from the dev-only endpoint renews it,
        // which also keeps a dev session alive across a reload.
        if (dev !== undefined && devSignInEmail !== undefined) {
          if (viaDevSignIn) return await dev.signIn(devSignInEmail)
          try {
            return await repository.refresh()
          } catch {
            return await dev.signIn(devSignInEmail)
          }
        }
        return await repository.refresh()
      } catch {
        // A rejected renewal is not an error to show: it is the sign-in state.
        return null
      }
    },
  })

  return {
    store,
    provider,
    repository,
    crossTab,
    dev,
    signIn: createSignIn({
      server: options.config.authorizationServer,
      transactions: createTransactionStore(options.storage),
      auth: repository,
      navigate: options.navigate,
    }),
    async signOut() {
      provider.signOut()
      await repository.signOut().catch(() => undefined)
    },
  }
}
